import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import { profesionalesApi, especialidadesApi } from '../../services/api';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import type { Profesional } from '../../types/api';

// Función helper para evaluar si un profesional está activo
const isProfessionalActive = (estado: any): boolean => {
  if (typeof estado === 'boolean') return estado;
  if (typeof estado === 'string') {
    return estado.toLowerCase() === 'activo' || estado.toLowerCase() === 'true' || estado === '1';
  }
  if (typeof estado === 'number') return estado === 1;
  return false;
};

// Componente Switch para el estado
function StatusSwitch({ 
  isActive, 
  onToggle, 
  loading = false 
}: { 
  isActive: boolean; 
  onToggle: () => void; 
  loading?: boolean; 
}) {
  return (
    <button
      onClick={onToggle}
      disabled={loading}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
        ${isActive ? 'bg-green-600' : 'bg-red-600'}
        ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <span
        className={`
          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
          ${isActive ? 'translate-x-6' : 'translate-x-1'}
        `}
      />
    </button>
  );
}

export function ProfessionalsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingProfessionalId, setEditingProfessionalId] = useState<number | null>(null);
  const [editingProfessional, setEditingProfessional] = useState<any>(null);
  const [newProfessional, setNewProfessional] = useState({
    nombre: '',
    apellidos: '',
    telefono: '',
    email: '',
    id_especialidad: '',
    id_calendario: '',
    estado: 'activo',
    fecha_alta: new Date().toISOString().split('T')[0],
    rol: ''
  });

  // Filtros y paginación
  const [inputFilters, setInputFilters] = useState({
    nombre: '',
    apellidos: '',
    estado: '',
    id_especialidad: '',
    fecha_inicio: '',
    fecha_fin: ''
  });
  const [filters, setFilters] = useState(inputFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Obtener lista de profesionales con filtros y paginación
  const { data, isLoading } = useQuery<any, Error>({
    queryKey: ['professionals', filters, page, limit],
    queryFn: () => profesionalesApi.getAll({ ...filters, page, limit }),
    staleTime: 1000 * 60
  });
  const professionals: Profesional[] = data?.data || [];
  const total: number = data?.total || 0;

  // Obtener lista de especialidades para el dropdown
  const { data: specialties } = useQuery({
    queryKey: ['specialties'],
    queryFn: especialidadesApi.getAll,
  });

  // Mutación para crear profesional
  const createMutation = useMutation({
    mutationFn: profesionalesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
      toast.success('Profesional creado exitosamente');
      setIsAdding(false);
      setNewProfessional({
        nombre: '',
        apellidos: '',
        telefono: '',
        email: '',
        id_especialidad: '',
        id_calendario: '',
        estado: 'activo',
        fecha_alta: new Date().toISOString().split('T')[0],
        rol: ''
      });
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Mutación para actualizar profesional
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      return profesionalesApi.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
      toast.success('Profesional actualizado exitosamente');
      setEditingProfessionalId(null);
      setEditingProfessional(null);
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Mutación para actualizar estado del profesional
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, estado }: { id: number; estado: string }) => {
      return profesionalesApi.update(id, { estado });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['professionals'] });
      toast.success('Estado del profesional actualizado');
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const handleToggleStatus = (professional: Profesional) => {
    const currentStatus = isProfessionalActive(professional.estado);
    const newStatus = currentStatus ? 'inactivo' : 'activo';
    updateStatusMutation.mutate({
      id: professional.id,
      estado: newStatus
    });
  };

  const handleAddNew = () => {
    setIsAdding(true);
  };

  const handleEdit = (professional: Profesional) => {
    setEditingProfessionalId(professional.id);
    setEditingProfessional({
      nombre: professional.nombre || '',
      apellidos: professional.apellidos || professional.apellido || '',
      telefono: professional.telefono || '',
      email: professional.email || '',
      id_especialidad: professional.id_especialidad || professional.especialidad_id || '',
      id_calendario: professional.id_calendario || '',
      estado: professional.estado || 'activo',
      fecha_alta: professional.fecha_alta || '',
      rol: professional.rol || ''
    });
  };

  const handleSaveNew = () => {
    if (!newProfessional.nombre || !newProfessional.apellidos || !newProfessional.email) {
      toast.error('Por favor, completa los campos obligatorios: Nombre, Apellidos y Email');
      return;
    }
    createMutation.mutate(newProfessional as any);
  };

  const handleSaveEdit = () => {
    if (!editingProfessional.nombre || !editingProfessional.apellidos || !editingProfessional.email) {
      toast.error('Por favor, completa los campos obligatorios: Nombre, Apellidos y Email');
      return;
    }
    updateMutation.mutate({
      id: editingProfessionalId!,
      data: editingProfessional
    });
  };

  const handleCancelNew = () => {
    setIsAdding(false);
    setNewProfessional({
      nombre: '',
      apellidos: '',
      telefono: '',
      email: '',
      id_especialidad: '',
      id_calendario: '',
      estado: 'activo',
      fecha_alta: new Date().toISOString().split('T')[0],
      rol: ''
    });
  };

  const handleCancelEdit = () => {
    setEditingProfessionalId(null);
    setEditingProfessional(null);
  };

  const handleInputChange = (field: string, value: any) => {
    setNewProfessional(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditInputChange = (field: string, value: any) => {
    setEditingProfessional((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  // Función para formatear fechas
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('es-ES');
    } catch {
      return dateString;
    }
  };

  // Función para obtener el nombre de la especialidad
  const getSpecialtyName = (specialtyId?: number) => {
    if (!specialtyId || !specialties) return '-';
    const specialty = specialties.data.find((s: { id: number }) => s.id === specialtyId);
    return specialty?.nombre || '-';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text={t('professionals.loadingProfessionals')} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('professionals.title')}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Gestiona la información de los profesionales de la clínica
          </p>
        </div>
        <button
          onClick={handleAddNew}
          disabled={isAdding || editingProfessionalId !== null}
          className="btn-primary disabled:opacity-50"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          {t('professionals.addProfessional')}
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-4 items-end bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Nombre</label>
          <input type="text" value={inputFilters.nombre} onChange={e => setInputFilters(f => ({ ...f, nombre: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Apellidos</label>
          <input type="text" value={inputFilters.apellidos} onChange={e => setInputFilters(f => ({ ...f, apellidos: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Estado</label>
          <select value={inputFilters.estado} onChange={e => setInputFilters(f => ({ ...f, estado: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100">
            <option value="">Todos</option>
            <option value="ACTIVO">ACTIVO</option>
            <option value="INACTIVO">INACTIVO</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Especialidad</label>
          <select value={inputFilters.id_especialidad} onChange={e => setInputFilters(f => ({ ...f, id_especialidad: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100">
            <option value="">Todas</option>
            {specialties?.data?.map((s: { id: number; nombre: string }) => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Fecha alta desde</label>
          <input type="date" value={inputFilters.fecha_inicio} onChange={e => setInputFilters(f => ({ ...f, fecha_inicio: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Fecha alta hasta</label>
          <input type="date" value={inputFilters.fecha_fin} onChange={e => setInputFilters(f => ({ ...f, fecha_fin: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100" />
        </div>
        <div>
          <button onClick={() => { setFilters(inputFilters); setPage(1); }} className="btn btn-sm bg-blue-600 hover:bg-blue-700 text-white">Buscar</button>
          <button onClick={() => { setInputFilters({ nombre: '', apellidos: '', estado: '', id_especialidad: '', fecha_inicio: '', fecha_fin: '' }); setFilters({ nombre: '', apellidos: '', estado: '', id_especialidad: '', fecha_inicio: '', fecha_fin: '' }); setPage(1); }} className="btn btn-sm ml-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200">Limpiar</button>
        </div>
      </div>

      {/* Tabla de profesionales */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Apellidos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Teléfono
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Especialidad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ID Calendario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fecha Alta
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Fila para agregar nuevo profesional */}
              {isAdding && (
                <tr className="bg-blue-50 dark:bg-blue-900/20">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    Auto
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="text"
                      value={newProfessional.nombre}
                      onChange={(e) => handleInputChange('nombre', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Nombre *"
                      required
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="text"
                      value={newProfessional.apellidos}
                      onChange={(e) => handleInputChange('apellidos', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Apellidos *"
                      required
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="tel"
                      value={newProfessional.telefono}
                      onChange={(e) => handleInputChange('telefono', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Teléfono"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="email"
                      value={newProfessional.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Email *"
                      required
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={newProfessional.id_especialidad}
                      onChange={(e) => handleInputChange('id_especialidad', e.target.value ? Number(e.target.value) : '')}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                    >
                      <option value="">Seleccionar</option>
                      {specialties?.data?.map((s: { id: number; nombre: string }) => (
                        <option key={s.id} value={s.id}>
                          {s.nombre}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      value={newProfessional.id_calendario}
                      onChange={(e) => handleInputChange('id_calendario', e.target.value ? Number(e.target.value) : '')}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="ID Calendario"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                      Activo
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(newProfessional.fecha_alta)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="text"
                      value={newProfessional.rol}
                      onChange={(e) => handleInputChange('rol', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Rol"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveNew}
                        disabled={createMutation.isPending}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                        title="Guardar"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleCancelNew}
                        disabled={createMutation.isPending}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                        title="Cancelar"
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {/* Filas de profesionales existentes */}
              {professionals?.map((professional) => (
                <tr key={professional.id} className={editingProfessionalId === professional.id ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {professional.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {editingProfessionalId === professional.id ? (
                      <input
                        type="text"
                        value={editingProfessional.nombre}
                        onChange={(e) => handleEditInputChange('nombre', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Nombre *"
                        required
                      />
                    ) : (
                      professional.nombre
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {editingProfessionalId === professional.id ? (
                      <input
                        type="text"
                        value={editingProfessional.apellidos}
                        onChange={(e) => handleEditInputChange('apellidos', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Apellidos *"
                        required
                      />
                    ) : (
                      professional.apellidos || professional.apellido
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingProfessionalId === professional.id ? (
                      <input
                        type="tel"
                        value={editingProfessional.telefono}
                        onChange={(e) => handleEditInputChange('telefono', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Teléfono"
                      />
                    ) : (
                      professional.telefono || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingProfessionalId === professional.id ? (
                      <input
                        type="email"
                        value={editingProfessional.email}
                        onChange={(e) => handleEditInputChange('email', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Email *"
                        required
                      />
                    ) : (
                      professional.email || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingProfessionalId === professional.id ? (
                      <select
                        value={editingProfessional.id_especialidad}
                        onChange={(e) => handleEditInputChange('id_especialidad', e.target.value ? Number(e.target.value) : '')}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="">Seleccionar</option>
                        {specialties?.data?.map((s: { id: number; nombre: string }) => (
                          <option key={s.id} value={s.id}>
                            {s.nombre}
                          </option>
                        ))}
                      </select>
                    ) : (
                      getSpecialtyName(professional.id_especialidad || professional.especialidad_id)
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingProfessionalId === professional.id ? (
                      <input
                        type="number"
                        value={editingProfessional.id_calendario}
                        onChange={(e) => handleEditInputChange('id_calendario', e.target.value ? Number(e.target.value) : '')}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="ID Calendario"
                      />
                    ) : (
                      professional.id_calendario || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingProfessionalId === professional.id ? (
                      <select
                        value={editingProfessional.estado}
                        onChange={(e) => handleEditInputChange('estado', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="ACTIVO">ACTIVO</option>
                        <option value="INACTIVO">INACTIVO</option>
                      </select>
                    ) : (
                      <StatusSwitch
                        isActive={isProfessionalActive(professional.estado)}
                        onToggle={() => handleToggleStatus(professional)}
                        loading={updateStatusMutation.isPending}
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingProfessionalId === professional.id ? (
                      <input
                        type="date"
                        value={editingProfessional.fecha_alta}
                        onChange={(e) => handleEditInputChange('fecha_alta', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      />
                    ) : (
                      formatDate(professional.fecha_alta)
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingProfessionalId === professional.id ? (
                      <input
                        type="text"
                        value={editingProfessional.rol}
                        onChange={(e) => handleEditInputChange('rol', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Rol"
                      />
                    ) : (
                      professional.rol || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {editingProfessionalId === professional.id ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            disabled={updateMutation.isPending}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                            title="Guardar"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={updateMutation.isPending}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                            title="Cancelar"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleEdit(professional)}
                          disabled={isAdding || editingProfessionalId !== null}
                          className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 disabled:opacity-50"
                          title="Editar"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(!professionals || professionals.length === 0) && !isAdding && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {t('common.noData')}
            </p>
          </div>
        )}
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {professionals.length} de {total} profesionales
        </div>
        <div className="flex items-center gap-2">
          <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="btn btn-xs">Anterior</button>
          <span className="text-sm">Página {page}</span>
          <button disabled={page * limit >= total} onClick={() => setPage(p => p + 1)} className="btn btn-xs">Siguiente</button>
          <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} className="input input-xs ml-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
            {[5, 10, 20, 50].map(opt => <option key={opt} value={opt}>{opt} por página</option>)}
          </select>
        </div>
      </div>
    </div>
  );
} 